// Fachsprache und Aufrufe der Zugehoerigkeit (ST-REL-001, erster Schnitt;
// CM-PTY-010; ADR-0054) -- seit dem zweiten Schnitt (ADR-0057) auch Aendern
// und Beenden einer Rolle.
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in
// classifications.ts und interactions.ts.
//
// DIE WOERTER "CONTEXT", "ROLE CODE", "ASSIGNMENT", "OPERATING CONTEXT" UND
// "GROUPWIDE" KOMMEN AUF DEM BILDSCHIRM NICHT VOR (NFR-UX-003, CM-UX-010). Auf
// dem Bildschirm heisst es "Haus", "Rolle", "zuordnen", "sichtbar". Eine Probe
// (context-roles.test.ts) haelt jeden Satz dieser Datei gegen eine
// Verbotsliste.
//
// KEIN ENTWURF IM BROWSER: die Eingabe ist eine Auswahl, kein Text.

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';
import type { RoleNames } from './context-role-names.ts';
import { artWort, hausArtOf, type HausArt } from './haus-art.ts';

export type SendLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Die sieben Grundrollen in Worten -- dieselben Codes wie das CHECK aus
 * Migration 0042. Personenform mit Doppelpunkt (Hausregel, Glossar). Der
 * Abschnitt "Rolle im Haus" liest Namen und Auswahl aus dem Rollenkatalog
 * (context-role-names.ts); diese Liste traegt nur noch Suche und Kontaktfilter.
 */
export const ROLE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  author: 'Autor:in',
  editor: 'Lektor:in',
  agent: 'Agent:in',
  reviewer: 'Rezensent:in',
  journalist: 'Journalist:in',
  bookseller: 'Buchhändler:in',
  sales_contact: 'Ansprechpartner:in im Vertrieb',
});

/**
 * Die Schreibweisen einer Rolle, die eine Eingabe treffen darf: die Doppelpunktform
 * des Glossars (Hausregel) und die beiden ausgeschriebenen Formen, die Menschen
 * tippen -- „Autor:in“, „Autorin“, „Autor“. Die Reihenfolge ist die Vorrangfolge,
 * wenn eine Korrektur zwei Formen gleich nah findet.
 */
export function roleLabelSpellings(label: string): readonly string[] {
  if (!label.includes(':in')) return [label];
  return [label, label.replace(':in', 'in'), label.replace(':in', '')];
}

/** Rueckfall: der Rohwert sichtbar unuebersetzt, statt ein erfundenes Wort. */
export function roleLabel(code: string): string {
  return ROLE_LABELS[code] ?? code;
}

export interface ContextRoleView {
  readonly contextRoleAssignmentId: string;
  readonly roleCode: string;
  readonly operatingContextId: string;
  readonly operatingContextDisplayName: string;
  /** Die Art des Hauses (M4) fuer die Saetze, die es nicht beim Namen nennen; null heisst "Verlag". */
  readonly operatingContextKind?: HausArt | null;
  readonly validFrom: string;
  /** Der Stand fuer Aendern und Beenden (ADR-0057); ein abweichender Stand ist ein 409. */
  readonly lockVersion: number;
  /** Das geplante Ende einer aktiven Rolle, oder null fuer unbefristet (ADR-0057). */
  readonly validTo: string | null;
}

export type AssignmentView =
  | { readonly available: true; readonly operatingContextId: string; readonly operatingContextDisplayName: string; readonly operatingContextKind: HausArt | null }
  | { readonly available: false; readonly unavailableReason: string };

export type ContextRolesResult =
  | { readonly outcome: 'loaded'; readonly contextRoles: readonly ContextRoleView[]; readonly assignment: AssignmentView }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type AssignRoleResult =
  | { readonly outcome: 'assigned' }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'invalid' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage; readonly reasonCode?: string | undefined };

// --- Die Saetze: Problem, Wirkung, naechster Schritt (NFR-UX-005) -----------

/**
 * Der Hinweis unter den Rollen. Er sagt nichts ueber die Sichtbarkeit: seit
 * ADR-0064 Ziffer 2 traegt eine Rolle keine, die steht als Klasse am Kontakt.
 * Die Aussage "Hauskontakt" oder "gemeinsamer Kontakt" kommt mit dem Schnitt,
 * der die Klasse im Leseweg ausliefert (ST-SEC-001 S3/S4).
 * Kein fremdes Haus wird genannt -- der Leseweg kennt nur die eigenen (TM-010).
 */
export function visibilityStatement(roleCount: number): string {
  return roleCount === 0 ? 'Dieser Kontakt hat noch keine Rolle.' : 'Eine Rolle beschreibt, was dieser Kontakt für einen Verlag, eine Holding oder einen Vertrieb ist.';
}

/** Die Bestaetigung NACH dem Zuordnen -- fuer die Statusregion des Abschnitts. */
export function assignedAnnouncement(roleName: string, houseName: string): string {
  return `Rolle „${roleName}" für ${houseName} zugeordnet.`;
}

export function roleLine(role: ContextRoleView, formattedSince: string, names: RoleNames): string {
  return `${names(role.roleCode)} bei ${role.operatingContextDisplayName}, seit ${formattedSince}`;
}

/** Gross am Satzanfang: "Dein Verlag", "Deine Holding". */
function dein(art: HausArt | null | undefined): string {
  const wort = artWort(art, 'dein');
  return wort.charAt(0).toUpperCase() + wort.slice(1);
}

// Die Saetze ueber das eigene Haus nennen dessen Art (A2, M4); ohne Art heisst es "Verlag".

export function duplicateRoleMessage(art: HausArt | null | undefined): PlainMessage {
  return Object.freeze({
    headline: 'Diese Rolle ist bereits zugeordnet.',
    explanation: `Der Kontakt hat diese Rolle in ${artWort(art, 'deinem')} schon; ein zweites Mal wird sie nicht zugeordnet.`,
    nextStep: 'Die Liste oben zeigt den gespeicherten Stand.',
    retryable: false,
    // Der gewollte Zustand gilt schon: ein Hinweis, kein Fehler.
    notice: true,
  });
}

export const PARTY_NOT_WRITABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'An diesem Kontakt können keine Rollen mehr zugeordnet werden.',
  explanation: 'Der Kontakt ist zusammengeführt, archiviert oder anonymisiert. Sein Stand ist abgeschlossen.',
  nextStep: 'Öffne den aktuellen Kontakt über die Kontaktliste.',
  retryable: false,
});

/**
 * Mehrere Haeuser: die benannte Grenze des ersten Schritts, dieselbe wie bei
 * der Notiz (ADR-0051 Ziffer 2). Der Satz sagt, warum -- und was jetzt geht.
 */
export const HOUSE_AMBIGUOUS_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Für welche Gesellschaft der Gruppe die Rolle gilt, lässt sich hier noch nicht wählen.',
  explanation: 'Du arbeitest für mehrere Gesellschaften der Gruppe. LIAISON ordnet eine Rolle nur zu, wenn eindeutig ist, für welche sie gilt.',
  nextStep: 'Wird die Rolle jetzt gebraucht, wende dich an die LIAISON-Administration.',
  retryable: false,
});

export function houseInactiveMessage(art: HausArt | null | undefined): PlainMessage {
  return Object.freeze({
    headline: `${dein(art)} ist in LIAISON derzeit nicht aktiv.`,
    explanation: `In ${artWort(art, 'einem')}, ${artWort(art, 'der')} nicht aktiv ist, ordnet LIAISON keine Rollen zu.`,
    nextStep: 'Wende dich an die LIAISON-Administration.',
    retryable: false,
  });
}

/**
 * Das Haus hat sich zwischen Anzeige und Absenden geaendert (Codex-Cloud-Review
 * #262, #365): der Abschnitt zeigte ein anderes Haus als das, das LIAISON jetzt
 * ableitet. Nichts ist geschrieben; der Abschnitt laedt neu und zeigt das
 * aktuelle Haus.
 */
export const HOUSE_CHANGED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Deine Zuordnung hat sich gerade geändert.',
  explanation: 'Der Abschnitt zeigte einen anderen Verlag als den, für den du jetzt freigeschaltet bist. Es wurde nichts zugeordnet.',
  nextStep: 'Prüfe den Namen im Abschnitt und ordne die Rolle erneut zu.',
  retryable: false,
});

/**
 * Ein 409 mit einem Grundcode, den diese Oberflaeche nicht kennt -- etwa von
 * einem Dienst in einem anderen Stand. Der Satz nennt die Handlung und den
 * Schritt, der dann hilft, statt eine Ursache zu erfinden.
 */
export const ROLE_NOT_ASSIGNED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Rolle wurde nicht zugeordnet.',
  explanation: 'LIAISON hat die Zuordnung abgelehnt, ohne einen Grund zu nennen, den diese Seite kennt.',
  nextStep: 'Lade die Seite neu und ordne die Rolle erneut zu.',
  retryable: false,
});

/**
 * Die gewaehlte Rolle ist nicht mehr vergebbar (409 context_role_type_not_assignable).
 * Die Auswahl bietet nur vergebbare Rollen an; der Fall heisst also: zwischen
 * Laden und Speichern beendet. Der Abschnitt laedt den Katalog dann neu.
 */
export const ROLE_TYPE_NOT_ASSIGNABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Rolle lässt sich gerade nicht vergeben.',
  explanation: 'Sie wurde inzwischen beendet.',
  nextStep: 'Die Auswahl ist neu geladen. Wähle eine andere Rolle.',
  retryable: false,
});

/** Dasselbe, wenn es die letzte freie Rolle war: keine andere zum Waehlen, das Formular ist zu. */
export const ROLE_TYPE_ENDED_NONE_LEFT_MESSAGE: PlainMessage = Object.freeze({
  ...ROLE_TYPE_NOT_ASSIGNABLE_MESSAGE,
  nextStep: 'Gerade lässt sich keine andere Rolle vergeben.',
});

/** Ohne Zuordnung liefert der Leseweg kein Haus und damit keine Art. */
export function unavailableMessage(reason: string): PlainMessage {
  return reason === 'context_not_active' ? houseInactiveMessage(null) : HOUSE_AMBIGUOUS_MESSAGE;
}

/**
 * Die Rollen, die im eigenen Haus noch zugeordnet werden koennen: die
 * vergebbaren des Katalogs (`catalogAssignable`, in dessen Reihenfolge) ohne
 * die, die das Haus schon hat.
 */
export function assignableRoles(roles: readonly ContextRoleView[], operatingContextId: string, catalogAssignable: readonly string[]): readonly string[] {
  const vergeben = new Set(roles.filter((role) => role.operatingContextId === operatingContextId).map((role) => role.roleCode));
  return catalogAssignable.filter((code) => !vergeben.has(code));
}

// --- Die Aufrufe -----------------------------------------------------------

function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return { status: response.status, answered: payload !== undefined, ...correlationOf(body) };
}

function reasonCodeOf(payload: unknown): string | undefined {
  const body = (payload ?? {}) as Record<string, unknown>;
  return typeof body.reasonCode === 'string' ? body.reasonCode : undefined;
}

function roleOf(value: unknown): ContextRoleView | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (
    typeof row.contextRoleAssignmentId !== 'string' ||
    typeof row.roleCode !== 'string' ||
    typeof row.operatingContextId !== 'string' ||
    typeof row.operatingContextDisplayName !== 'string' ||
    typeof row.validFrom !== 'string' ||
    typeof row.lockVersion !== 'number' ||
    !Number.isSafeInteger(row.lockVersion) ||
    row.lockVersion < 1 ||
    (row.validTo !== null && typeof row.validTo !== 'string')
  ) {
    return undefined;
  }
  return {
    contextRoleAssignmentId: row.contextRoleAssignmentId,
    roleCode: row.roleCode,
    operatingContextId: row.operatingContextId,
    operatingContextDisplayName: row.operatingContextDisplayName,
    operatingContextKind: hausArtOf(row.operatingContextKind),
    validFrom: row.validFrom,
    lockVersion: row.lockVersion,
    validTo: row.validTo,
  };
}

function assignmentOf(value: unknown): AssignmentView {
  const row = (value ?? {}) as Record<string, unknown>;
  if (row.available === true && typeof row.operatingContextId === 'string' && typeof row.operatingContextDisplayName === 'string') {
    return {
      available: true,
      operatingContextId: row.operatingContextId,
      operatingContextDisplayName: row.operatingContextDisplayName,
      operatingContextKind: hausArtOf(row.operatingContextKind),
    };
  }
  // Fail-closed: eine Antwort, die den Vertrag bricht, bietet keine Zuordnung an.
  return { available: false, unavailableReason: typeof row.unavailableReason === 'string' ? row.unavailableReason : 'context_ambiguous' };
}

export async function fetchContextRoles(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}): Promise<ContextRolesResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/context-roles`, {
    headers: { authorization: `Bearer ${options.accessToken}` },
  });
  const payload = await response.json().catch(() => undefined);
  if (response.status === 404) {
    return { outcome: 'not-found' };
  }
  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  const body = (payload ?? {}) as Record<string, unknown>;
  const contextRoles: ContextRoleView[] = [];
  for (const row of Array.isArray(body.contextRoles) ? body.contextRoles : []) {
    const view = roleOf(row);
    if (view !== undefined) contextRoles.push(view);
  }
  return { outcome: 'loaded', contextRoles, assignment: assignmentOf(body.assignment) };
}

export async function assignContextRole(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly roleCode: string;
  /** Das Haus, dessen Namen der Abschnitt zeigte -- ein Erwartungswert, keine Wahl. */
  readonly expectedOperatingContextId: string;
  /** Die Art dieses Hauses, fuer die Saetze, die es nicht beim Namen nennen. */
  readonly art?: HausArt | null;
  readonly send?: SendLike;
}): Promise<AssignRoleResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/context-roles`, {
    method: 'POST',
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify({
      roleCode: options.roleCode,
      expectedOperatingContextId: options.expectedOperatingContextId,
    }),
  });
  const payload = await response.json().catch(() => undefined);
  if (response.status === 201) {
    return { outcome: 'assigned' };
  }
  const reasonCode = reasonCodeOf(payload);
  if (response.status === 404) {
    return { outcome: 'not-found' };
  }
  if (response.status === 422) {
    return { outcome: 'invalid' };
  }
  if (response.status === 409) {
    // Nur die Grundcodes, die der Schreibweg sendet; ein unbekannter (etwa
    // aus einer API vor ADR-0064) bekommt einen eigenen Satz statt eines
    // falschen fachlichen.
    const message =
      reasonCode === 'context_role_duplicate'
        ? duplicateRoleMessage(options.art)
        : reasonCode === 'party_not_writable'
          ? PARTY_NOT_WRITABLE_MESSAGE
          : reasonCode === 'context_role_context_ambiguous'
            ? HOUSE_AMBIGUOUS_MESSAGE
            : reasonCode === 'operating_context_not_active'
              ? houseInactiveMessage(options.art)
              : reasonCode === 'context_role_context_changed'
                ? HOUSE_CHANGED_MESSAGE
                : reasonCode === 'context_role_type_not_assignable'
                  ? ROLE_TYPE_NOT_ASSIGNABLE_MESSAGE
                  : ROLE_NOT_ASSIGNED_MESSAGE;
    return { outcome: 'rejected', message, reasonCode };
  }
  return { outcome: 'rejected', message: plainMessageFor(reasonCode, factsOf(response, payload)), reasonCode };
}

// --- Aendern und Beenden (ST-REL-001, zweiter Schnitt; ADR-0057) -------------
//
// DIE ZUSAGE, in Worten: Aendern und Beenden aendern nie, wer den Kontakt
// sehen kann. Deshalb gibt es hier keinen Bestaetigungsdialog -- beides ist
// ueber denselben Abschnitt umkehrbar -- und "Beenden" erscheint nur, wenn das
// Haus danach noch eine Rolle hat (R-S2-1). Die letzte Rolle laesst sich nur
// aendern.

export type RoleChangeResult =
  | { readonly outcome: 'changed' }
  | { readonly outcome: 'ended' }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'invalid' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage; readonly reasonCode?: string | undefined };

/**
 * Beenden wird nur angeboten, wenn das Haus der Rolle danach noch eine
 * UNBEFRISTETE hat -- dieselbe Grenze wie COUNT_REMAINING_IN_HOUSE_SQL im
 * Schreibweg (Codex-Cloud-Review #267): eine Rolle mit geplantem Ende zaehlt
 * dort nicht als Rest, und eine Schaltflaeche, die immer mit 409 endet, waere
 * eine Aktion, die es nicht gibt.
 */
export function canEndRole(roles: readonly ContextRoleView[], role: ContextRoleView): boolean {
  return roles.some(
    (other) =>
      other.contextRoleAssignmentId !== role.contextRoleAssignmentId &&
      other.operatingContextId === role.operatingContextId &&
      other.validTo === null,
  );
}

/** Die Rollen, auf die eine Rolle geaendert werden kann: im selben Haus noch frei. */
export function changeTargets(roles: readonly ContextRoleView[], role: ContextRoleView, catalogAssignable: readonly string[]): readonly string[] {
  return assignableRoles(roles, role.operatingContextId, catalogAssignable);
}

export function endedAnnouncement(role: ContextRoleView, names: RoleNames): string {
  // Ehrlich und mit Rueckweg (Experience-Review 28.09.2026, P1/P2): die
  // beendete Rolle ist gespeichert, aber hier nicht mehr zu sehen -- eine
  // Historienansicht hat dieser Schnitt nicht. Ein Fehlklick heilt ueber
  // "Rolle zuordnen" im selben Abschnitt.
  return `Rolle „${names(role.roleCode)}" bei ${role.operatingContextDisplayName} beendet. Sie bleibt gespeichert, erscheint hier aber nicht mehr. War das ein Versehen, ordne sie über „Rolle zuordnen" erneut zu.`;
}

export function changedAnnouncement(role: ContextRoleView, nextRoleCode: string, names: RoleNames): string {
  return `Rolle bei ${role.operatingContextDisplayName} geändert: jetzt „${names(nextRoleCode)}" statt „${names(role.roleCode)}".`;
}

export function changeButtonLabel(role: ContextRoleView, names: RoleNames): string {
  return `Rolle „${names(role.roleCode)}" bei ${role.operatingContextDisplayName} ändern`;
}

export function endButtonLabel(role: ContextRoleView, names: RoleNames): string {
  return `Rolle „${names(role.roleCode)}" bei ${role.operatingContextDisplayName} beenden`;
}

/**
 * Die letzte Rolle im Haus (R-S2-1). Der Satz nennt den Grund OHNE fremde
 * Haeuser -- ob andere den Kontakt halten, weiss diese Oberflaeche nicht und
 * soll sie nicht verraten (TM-010).
 */
export function lastInHouseMessage(art: HausArt | null | undefined): PlainMessage {
  return Object.freeze({
    headline: `Die einzige Rolle ${artWort(art, 'deines')} lässt sich nicht beenden.`,
    explanation: `Ohne sie gehörte der Kontakt nicht mehr zu ${artWort(art, 'deinem')}. Es wurde nichts geändert.`,
    nextStep: `Passt die Rolle nicht, ändere sie über „Ändern". Soll der Kontakt nicht mehr zu ${artWort(art, 'deinem')} gehören, wende dich an die LIAISON-Administration.`,
    retryable: false,
  });
}

/** Die Wiederholung eines schon gespeicherten Endes -- ein Hinweis, kein Fehler. */
export const ALREADY_ENDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Rolle ist bereits beendet.',
  explanation: 'Das Ende war schon gespeichert, vielleicht aus einem anderen Fenster.',
  nextStep: 'Die Liste zeigt jetzt den aktuellen Stand.',
  retryable: false,
  // Der gewollte Zustand gilt schon: ein Hinweis, kein Fehler.
  notice: true,
});

export const ROLE_OUTDATED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Rolle wurde inzwischen geändert.',
  explanation: 'Jemand hat sie nach dem Laden bearbeitet. Es wurde nichts geändert.',
  nextStep: 'Die Liste ist neu geladen. Prüfe den Stand und versuche es erneut.',
  retryable: false,
});

export function roleGoneMessage(art: HausArt | null | undefined): PlainMessage {
  return Object.freeze({
    headline: 'Diese Rolle gibt es hier nicht mehr.',
    explanation: `Sie ist inzwischen beendet oder gehört nicht zu ${artWort(art, 'deinem')}. Es wurde nichts geändert.`,
    nextStep: 'Die Liste ist neu geladen und zeigt den aktuellen Stand.',
    retryable: false,
  });
}

/**
 * Die zweite Haelfte des Wechsels scheiterte (R-S2-3: die Meldung nennt die
 * Haelfte): die neue Rolle ist nicht zugeordnet, und die bisherige besteht
 * UNVERAENDERT weiter -- es gibt keinen halben Zustand.
 */
export function nextRoleRejectedMessage(nextRoleName: string, reasonCode: string | undefined, art?: HausArt | null): PlainMessage {
  const grund =
    reasonCode === 'operating_context_not_active'
      ? `${dein(art)} ist in LIAISON derzeit nicht aktiv.`
      : reasonCode === 'context_role_unchanged'
        ? `Der Kontakt hat die Rolle „${nextRoleName}" hier bereits.`
        : reasonCode === 'context_role_type_not_assignable'
          ? ROLE_TYPE_NOT_ASSIGNABLE_MESSAGE.explanation
          : `Der Kontakt hat die Rolle „${nextRoleName}" in ${artWort(art, 'deinem')} schon.`;
  return Object.freeze({
    headline:
      reasonCode === 'context_role_type_not_assignable'
        ? `„${nextRoleName}" lässt sich gerade nicht vergeben.`
        : 'Die neue Rolle wurde nicht zugeordnet.',
    explanation: `${grund} Die bisherige Rolle besteht unverändert weiter.`,
    nextStep:
      reasonCode === 'operating_context_not_active'
        ? 'Wende dich an die LIAISON-Administration.'
        : reasonCode === 'context_role_type_not_assignable'
          ? ROLE_TYPE_NOT_ASSIGNABLE_MESSAGE.nextStep
          : 'Die Liste zeigt den gespeicherten Stand. Wähle eine andere Rolle.',
    retryable: false,
  });
}

/** Die erste Haelfte des Wechsels scheiterte: die bisherige Rolle ist nicht angetastet. */
function previousRoleRejectedMessage(message: PlainMessage): PlainMessage {
  return Object.freeze({ ...message, headline: `Die Rolle wurde nicht geändert. ${message.headline}` });
}

// context_role_type_not_assignable: die NEUE Rolle ist nicht mehr vergebbar (Schreibweg: Haelfte assign_next).
const NEXT_HALF_REASONS = new Set(['context_role_duplicate', 'context_role_unchanged', 'operating_context_not_active', 'context_role_type_not_assignable']);

function lifecycleMessage(reasonCode: string | undefined, art: HausArt | null | undefined): PlainMessage | undefined {
  switch (reasonCode) {
    case 'context_role_last_in_house':
      return lastInHouseMessage(art);
    case 'context_role_already_ended':
      return ALREADY_ENDED_MESSAGE;
    case 'context_role_outdated':
      return ROLE_OUTDATED_MESSAGE;
    case 'party_not_writable':
      return PARTY_NOT_WRITABLE_MESSAGE;
    default:
      return undefined;
  }
}

async function sendLifecycle(options: {
  readonly url: string;
  readonly accessToken: string;
  readonly body: Record<string, unknown>;
  readonly send?: SendLike | undefined;
  readonly okStatus: number;
  readonly applied: RoleChangeResult;
  /** Der Name der neuen Rolle -- nur beim Wechsel, fuer den Satz zur zweiten Haelfte. */
  readonly nextRoleName?: string;
  /** Die Art des Hauses der Rolle. */
  readonly art: HausArt | null | undefined;
}): Promise<RoleChangeResult> {
  const send = options.send ?? fetch;
  const response = await send(options.url, {
    method: 'POST',
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => undefined);
  if (response.status === options.okStatus) {
    return options.applied;
  }
  const reasonCode = reasonCodeOf(payload);
  if (response.status === 404) {
    // Unbekannte, fremde oder beendete Zuordnung -- oder der Kontakt selbst ist
    // weg. Die Oberflaeche unterscheidet das am Grundcode.
    if (reasonCode === 'context_role_not_found') {
      const gone = roleGoneMessage(options.art);
      const message = options.nextRoleName === undefined ? gone : previousRoleRejectedMessage(gone);
      return { outcome: 'rejected', message, reasonCode };
    }
    return { outcome: 'not-found' };
  }
  if (response.status === 422) {
    return { outcome: 'invalid' };
  }
  if (response.status === 409) {
    if (options.nextRoleName !== undefined && reasonCode !== undefined && NEXT_HALF_REASONS.has(reasonCode)) {
      return { outcome: 'rejected', message: nextRoleRejectedMessage(options.nextRoleName, reasonCode, options.art), reasonCode };
    }
    const message = lifecycleMessage(reasonCode, options.art) ?? ROLE_OUTDATED_MESSAGE;
    return {
      outcome: 'rejected',
      message: options.nextRoleName === undefined || reasonCode === 'party_not_writable' ? message : previousRoleRejectedMessage(message),
      reasonCode,
    };
  }
  return { outcome: 'rejected', message: plainMessageFor(reasonCode, factsOf(response, payload)), reasonCode };
}

function roleUrl(baseUrl: string, partyId: string, role: ContextRoleView, action: 'end' | 'change'): string {
  return `${baseUrl}/parties/${encodeURIComponent(partyId)}/context-roles/${encodeURIComponent(role.contextRoleAssignmentId)}/${action}`;
}

export async function endContextRole(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly role: ContextRoleView;
  readonly send?: SendLike;
}): Promise<RoleChangeResult> {
  return sendLifecycle({
    url: roleUrl(options.baseUrl, options.partyId, options.role, 'end'),
    accessToken: options.accessToken,
    body: { lockVersion: options.role.lockVersion },
    send: options.send,
    okStatus: 200,
    applied: { outcome: 'ended' },
    art: options.role.operatingContextKind,
  });
}

export async function changeContextRole(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly role: ContextRoleView;
  readonly nextRoleCode: string;
  readonly names: RoleNames;
  readonly send?: SendLike;
}): Promise<RoleChangeResult> {
  return sendLifecycle({
    url: roleUrl(options.baseUrl, options.partyId, options.role, 'change'),
    accessToken: options.accessToken,
    body: { roleCode: options.nextRoleCode, lockVersion: options.role.lockVersion },
    send: options.send,
    okStatus: 201,
    applied: { outcome: 'changed' },
    nextRoleName: options.names(options.nextRoleCode),
    art: options.role.operatingContextKind,
  });
}
